import request from "utils/request";

import { baseRequestURL as baseURL, constructQueryParams } from "api/BaseAPI";
import { AibleClientFactory } from "client/aible/factory";

import { isEnabled } from "utils/featureFlags";

import { toast } from "react-toastify";

const baseRequestURL = baseURL + "v1/job/";
const testRequestURL = baseURL + "test/v1/job/";

export function loadModelMetadata(model) {
  if (model.metadata.url === undefined || typeof model.metadata.url != "string") {
    return Promise.resolve(model);
  }

  return fetch(model.metadata.url)
    .then((response) => response.json())
    .then((data) => ({ ...model, ...data }))
    .catch((e) => {
      console.log(e);
      return model;
    });
}

export function loadJobMetadata(job, models = undefined) {
  if (job.models === undefined || job.models.length === 0) {
    return job;
  }

  const jobModels = job.models;

  if (models) {
    job.models = jobModels.filter((jModel) => {
      return jModel && models.some((m) => m.model_name === jModel.model_name);
    });
  }

  if (job.models.some((model) => model.metadata.url !== undefined && typeof model.metadata.url == "string")) {
    return Promise.all(job.models.map((model) => loadModelMetadata(model))).then((models) => {
      job.models = models;

      return job;
    });
  }

  const accountClient = AibleClientFactory.getInstance().getAccountClient();

  let hasError = false;

  return accountClient
    .getCustomerClientFactory(job.account_id)
    .then((factory) => {
      const client = factory.getMetaClient();

      const model_names = job.models.map((model) => model.model_name);

      return client.getModelDetailList(job.id, model_names).catch((e) => {
        console.log(e);
        hasError = true;
        return [];
      });
    })
    .then((details) => {
      if (hasError && (job.status === "DONE" || job.status === "ERROR")) {
        toast(
          "There was an error loading the model details. Please refresh the page to try again or contact Aible Support at success@aible.com if the issue persists.",
          {
            type: toast.TYPE.ERROR,
            position: toast.POSITION.TOP_CENTER,
            autoClose: 7500
          }
        );
      }

      details = details.reduce((map, model) => {
        if (model && model.model_name !== undefined) {
          map[model.model_name] = model;
        }
        return map;
      }, {});

      job.models = job.models.map((model) => ({
        ...model,
        ...details[model.model_name]
      }));

      return job;
    })
    .catch((e) => {
      if (job.status === "DONE" || job.status === "ERROR") {
        toast(
          "There was an error loading the model details. Please refresh the page to try again or contact Aible Support at success@aible.com if the issue persists.",
          {
            type: toast.TYPE.ERROR,
            position: toast.POSITION.TOP_CENTER,
            autoClose: 7500
          }
        );
      }

      console.log(e);
      return job;
    });
}

export function runModels(
  errorCostRatio,
  setupState,
  datasetId,
  datasetAccountId,
  outcomeKey,
  outcomeValue,
  isProd,
  valueMatrix,
  nonTargeted,
  currentCapacity,
  dropColumns,
  dropRows,
  derivedColumns,
  tags,
  name,
  priceColumn,
  isForecast,
  modelNames,
  colTypes,
  forecastColumn,
  externalURL,
  testTrainSplitCondition,
  testTrainValidationCondition,
  forecastPeriodsOverride = undefined
) {
  let options = {
    error_cost_ratio: Math.abs(errorCostRatio),
    budget: 1.25,
    training_data_id: datasetId ? Number(datasetId) : datasetId,
    outcome_value: outcomeValue === undefined ? 0 : outcomeValue,
    outcome_key: outcomeKey,
    value_matrix: valueMatrix,
    max_ideal_population: nonTargeted === undefined ? 0 : nonTargeted,
    current_capacity: currentCapacity,
    extra: {
      setupState: setupState
    },
    name: name,
    account_id: datasetAccountId,
    drop_columns: dropColumns,
    drop_rows: dropRows,
    tags: tags,
    derived_columns: derivedColumns,
    column_type_overrides: colTypes
  };

  if (priceColumn) {
    options.score_function = {
      type: "column",
      key: priceColumn
    };
  }

  // Forecast
  // Presence of `timeseries_key` indicates that this is a forecast project
  // Adding periods for forecasts if passed a value into extra.periods due to routing through mjollnir
  if (isForecast) {
    if (forecastColumn) {
      options.timeseries_key = forecastColumn;
    }
    if (forecastPeriodsOverride) {
      options.extra = {
        ...options.extra,
        periods: forecastPeriodsOverride
      };
    }
  }

  if (testTrainSplitCondition) {
    options.train_split_condition = testTrainSplitCondition;
  }

  if (testTrainValidationCondition) {
    options.test_split_condition = testTrainValidationCondition;
  }

  if (modelNames !== undefined) {
    options.model_names = modelNames;
  }

  if (externalURL !== undefined && isEnabled("external-models")) {
    options.external_model_setup_url = externalURL;
  }

  return request(isProd ? baseRequestURL : testRequestURL, {
    method: "POST",
    body: JSON.stringify(options)
  }).then((response) => response);
}

export function getCloudURL(id) {
  return request(baseURL + "v1/job/" + id + "/debug", {
    method: "GET"
  }).then((response) => response);
}

export function deleteRun(id) {
  return request(baseURL + "v1/job/" + id, {
    method: "DELETE"
  }).then((response) => response);
}

export function cancelRun(id) {
  return request(baseURL + "v1/job/" + id + "/cancel", {
    method: "POST"
  }).then((response) => response);
}

export function updateRun(id, changes) {
  return request(baseURL + "v1/job/" + id, {
    method: "PATCH",
    body: JSON.stringify(changes)
  }).then((response) => response);
}

export function getRuns(accountId, { next, latestId, fetchAll, userId }) {
  let queryArr = [
    // ((userId || userId === 0) ? "created_by=" + userId : "")
  ];

  if (!fetchAll) {
    queryArr = [...queryArr, next ? "order_by=id&reverse=1&next=" + next : "", latestId ? "id=" + latestId : ""];
  }
  const requestStr = baseURL + "v1/accounts/" + accountId + "/jobs" + constructQueryParams(queryArr);

  return request(requestStr).then((response) => Promise.all(response.map((r) => loadJobMetadata(r))));
}

export function getRun(runId, models) {
  const requestStr = baseURL + "v1/job/" + runId + "?async=false";

  const options = {
    timeout: 30000
  };

  return request(requestStr, options).then((response) => loadJobMetadata(response, models));
}

export function getRunStatus(runId) {
  const requestStr = baseURL + "v1/job/" + runId + "?async=false&i=id&i=status&i=models.model_name";

  return request(requestStr).then((response) => response);
}

export function getModelOptions() {
  return request(baseURL + "v1/models/", {
    method: "GET"
  }).then((response) => response);
}
